import React from "react";
import styled from "styled-components";
import { Switch } from "antd";
import { ResponsiveAvatar } from "@/shared/components/Avatar/ResponsiveAvatar";
import { TAP_TARGET } from "@/shared/theme/breakpoints";
import { pressedTint } from "@/shared/theme/pressed";

export interface GroupCreatorCardRow {
	assignmentId: string;
	name: string;
	category: string;
	avatarUrl: string;
	/** Rendered by the page so the icon links match the desktop table. */
	socials: React.ReactNode;
	/** Rendered by the page so the reach-out status matches the desktop table. */
	interest: React.ReactNode;
	visible: boolean;
	selected: boolean;
	rate: string;
	convertedRate: string;
	rateError?: string;
	convertedRateError?: string;
}

export interface GroupCreatorCardsProps {
	rows: GroupCreatorCardRow[];
	rateLabel: string;
	convertedRateLabel: string;
	onToggleSelected: (assignmentId: string) => void;
	onToggleVisible: (assignmentId: string) => void;
	onRateChange: (assignmentId: string, type: "usd" | "aud", value: string) => void;
}

const List = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const Card = styled.li<{ $selected: boolean }>`
	background: #ffffff;
	border-radius: 20px;
	border: 1px solid ${(props) => (props.$selected ? "#c9c2f5" : "#eaeaea")};
	box-shadow: 0 4px 12px rgba(21, 89, 165, 0.04);
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	min-width: 0;
`;

const Identity = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	min-width: 0;
`;

const Avatar = styled(ResponsiveAvatar)`
	width: 48px;
	height: 48px;
	border-radius: 50%;
	object-fit: cover;
	flex-shrink: 0;
`;

const NameBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	flex: 1;
	min-width: 0;
`;

const Name = styled.span`
	font-size: 16px;
	font-weight: 600;
	color: #222;
	overflow-wrap: anywhere;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
`;

const Category = styled.span`
	font-size: 12px;
	color: #666;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

/** The native checkbox keeps its look; the label around it is the 44px hit area. */
const SelectTarget = styled.label`
	display: grid;
	place-items: center;
	width: ${TAP_TARGET}px;
	height: ${TAP_TARGET}px;
	margin: -8px -8px -8px 0;
	flex-shrink: 0;
	cursor: pointer;
	border-radius: 12px;
	${pressedTint}

	input {
		width: 20px;
		height: 20px;
		margin: 0;
		cursor: pointer;
		accent-color: #4460ef;
	}
`;

const StatusRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 4px 12px;
`;

const VisibleTarget = styled.label`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	min-height: ${TAP_TARGET}px;
	margin: -10px 0;
	font-size: 14px;
	color: #666;
	cursor: pointer;
`;

const Rates = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 12px;
`;

const RateField = styled.label`
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`;

const RateLabel = styled.span`
	font-size: 12px;
	color: #666;
`;

const RateInput = styled.input<{ $invalid?: boolean }>`
	box-sizing: border-box;
	width: 100%;
	height: ${TAP_TARGET}px;
	padding: 0 12px;
	border: 1px solid ${(props) => (props.$invalid ? "#ff4d4f" : "#d9d9d9")};
	border-radius: 8px;
	background: #fff;
	font-size: 16px;
	color: #222;

	&::placeholder {
		color: #bfbfbf;
	}

	&:focus {
		outline: none;
		border-color: ${(props) => (props.$invalid ? "#ff4d4f" : "#4460ef")};
		box-shadow: 0 0 0 2px ${(props) => (props.$invalid ? "rgba(255, 77, 79, 0.2)" : "rgba(68, 96, 239, 0.12)")};
	}
`;

const RateError = styled.span`
	font-size: 12px;
	line-height: 16px;
	color: #ff4d4f;
`;

/**
 * Phone view of the Add/edit Creator Group table: one card per creator with the same controls. The page
 * keeps all state and validation and passes rows and handlers in.
 */
export const GroupCreatorCards: React.FC<GroupCreatorCardsProps> = ({
	rows,
	rateLabel,
	convertedRateLabel,
	onToggleSelected,
	onToggleVisible,
	onRateChange,
}) => (
	<List aria-label="Creators">
		{rows.map((row) => (
			<Card key={row.assignmentId} $selected={row.selected}>
				<Identity>
					<Avatar displaySize={48} src={row.avatarUrl} alt={row.name} />
					<NameBlock>
						<Name>{row.name}</Name>
						<Category>{row.category}</Category>
					</NameBlock>
					{row.socials}
					<SelectTarget>
						<input
							type="checkbox"
							checked={row.selected}
							onChange={() => onToggleSelected(row.assignmentId)}
							aria-label={`Add ${row.name} to the group`}
						/>
					</SelectTarget>
				</Identity>

				<StatusRow>
					{row.interest}
					<VisibleTarget>
						Visible to client
						<Switch checked={row.visible} onChange={() => onToggleVisible(row.assignmentId)} size="small" />
					</VisibleTarget>
				</StatusRow>

				<Rates>
					<RateField>
						<RateLabel>{rateLabel}</RateLabel>
						<RateInput
							inputMode="decimal"
							placeholder="Input amount"
							value={row.rate}
							onChange={(e) => onRateChange(row.assignmentId, "usd", e.target.value)}
							$invalid={!!row.rateError}
							aria-invalid={!!row.rateError}
						/>
						{row.rateError && <RateError>{row.rateError}</RateError>}
					</RateField>
					<RateField>
						<RateLabel>{convertedRateLabel}</RateLabel>
						<RateInput
							inputMode="decimal"
							placeholder="Input amount"
							value={row.convertedRate}
							onChange={(e) => onRateChange(row.assignmentId, "aud", e.target.value)}
							$invalid={!!row.convertedRateError}
							aria-invalid={!!row.convertedRateError}
						/>
						{row.convertedRateError && <RateError>{row.convertedRateError}</RateError>}
					</RateField>
				</Rates>
			</Card>
		))}
	</List>
);
